import styled from 'styled-components';
import { motion } from 'framer-motion';
import { AppleLogo, GooglePlayLogo } from '@phosphor-icons/react';
import { Container } from '@/components/common/Container';
import { Section } from '@/components/common/Section';
import { theme } from '@/styles/theme';
import { pulseAnimation } from '@/styles/animations';
import { APP_STORE_URL, GOOGLE_PLAY_URL } from '@/utils/constants';
//@ts-ignore
import mascotCheeringImage from '@/assets/mascot_cheering.png';

const CTAWrapper = styled(Section)`
  background: linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${theme.colors.primary.dark} 100%);
  color: ${theme.colors.white};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${theme.spacing.xl};
  padding: ${theme.spacing.xxl} 0;
`;

const MascotImage = styled.img`
  width: 120px;
  height: auto;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
`;

const Title = styled.h2`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.white};
  margin: 0;

  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.fontSizes['3xl']};
  }
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.xl};
  color: ${theme.colors.primary.extraLight};
  margin: 0;
  max-width: 600px;
`;

const DownloadButtons = styled(motion.div)`
  display: flex;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    width: 100%;
    max-width: 300px;
  }
`;

const DownloadButton = styled.a<{ $variant: 'primary' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.semibold};
  border-radius: ${theme.borderRadius.full};
  text-decoration: none;
  transition: all ${theme.transitions.fast};

  ${({ $variant }) =>
    $variant === 'primary'
      ? `
    background: ${theme.colors.white};
    color: ${theme.colors.primary.main};

    &:hover {
      color: ${theme.colors.primary.main};
      transform: scale(1.05);
    }
  `
      : `
    background: ${theme.colors.neutral[800]};
    color: ${theme.colors.white};

    &:hover {
      color: ${theme.colors.white};
      transform: scale(1.05);
    }
  `}

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100%;
    justify-content: center;
  }
`;

export const FinalCTASection = () => {
  return (
    <CTAWrapper>
      <Container>
        <ContentWrapper>
          <MascotImage src={mascotCheeringImage} alt="Lexifox mascot cheering" />
          <Title>Start expanding your vocabulary today</Title>
          <Subtitle>Join thousands of learners. Free to start.</Subtitle>
          <DownloadButtons variants={pulseAnimation} animate="animate">
            <DownloadButton href={APP_STORE_URL} $variant="primary">
              <AppleLogo size={22} weight="fill" />
              App Store
            </DownloadButton>
            <DownloadButton href={GOOGLE_PLAY_URL} $variant="secondary">
              <GooglePlayLogo size={22} weight="fill" />
              Google Play
            </DownloadButton>
          </DownloadButtons>
        </ContentWrapper>
      </Container>
    </CTAWrapper>
  );
};
